<template>
	<nut-navbar
		class="navbar"
		fixed
		:placeholder="false"
		@on-click-back="goBack"
		leftShow
	>
		<template #content>
			<div style="color: #fff">用户注册</div>
		</template>
		<template #right>
			<nut-icon
				@click="handleOpenDialog(REGISTER_HELP)"
				class-prefix="icon"
				font-class-name="iconfont"
				name="question-circle"
			>
			</nut-icon>
		</template>
	</nut-navbar>
	<div class="container">
		<nut-form class="register-form" :model-value="formData" ref="formRef">
			<nut-form-item
				label="用户名"
				prop="username"
				required
				:rules="[{ required: true, message: '请输入用户名' }]"
			>
				<nut-input
					class="input"
					v-model="formData.username"
					placeholder="请输入用户名"
					type="text"
					max-length="16"
				>
				</nut-input>
			</nut-form-item>
			<nut-form-item
				label="手机号"
				prop="mobile"
				required
				:rules="[
					{ required: true, message: '请输入手机号' },
					{
						regex: mobileReg,
						message: '请输入正确的手机号',
					},
				]"
			>
				<nut-input
					class="input"
					v-model="formData.mobile"
					placeholder="请输入手机号"
					type="tel"
				>
				</nut-input>
			</nut-form-item>
			<nut-form-item
				label="验证码"
				prop="code"
				required
				:rules="[{ required: true, message: '请输入验证码' }]"
			>
				<nut-input
					class="input"
					v-model="formData.code"
					placeholder="请输入验证码"
					type="text"
				>
					<template #button>
						<nut-button
							@click="handleSend"
							:disabled="hasSendMsg"
							size="small"
							type="primary"
						>
							{{ hasSendMsg ? `${sendTime} s` : '获取验证码' }}
						</nut-button>
					</template>
				</nut-input>
			</nut-form-item>
			<nut-form-item
				label="密码"
				prop="password"
				required
				:rules="[
					{ required: true, message: '请设定密码' },
					{
						regex: passwordValidReg,
						message: '密码需包含字母和数字，长度不少于6位',
					},
				]"
			>
				<nut-input
					class="input"
					v-model="formData.password"
					placeholder="请设定密码"
					:type="showPassword ? 'text' : 'password'"
					:right-icon="showPassword ? 'eye' : 'eye-close'"
					class-prefix="icon"
					font-class-name="iconfont"
					right-icon-size="20"
					@change="handleCustomValidate"
					@click-right-icon="handleChangePasswordType"
				></nut-input>
			</nut-form-item>
		</nut-form>

		<p class="agree">
			<nut-icon
				:name="isChecked ? 'squarecheck' : 'square'"
				class="check-icon"
				font-class-name="iconfont"
				class-prefix="icon"
				@click="isChecked = !isChecked"
			/>
			我同意多动屏的
			<router-link class="link" to="/terms">服务条款</router-link>
		</p>

		<div class="btn-container">
			<nut-button
				class="btn"
				block
				type="primary"
				@click="handleRegister"
				:disabled="!isChecked"
				:loading="isLoading"
			>
				注册
			</nut-button>
		</div>
	</div>
</template>

<script>
import { reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { Toast } from '@nutui/nutui';
import MD5 from 'crypto-js/md5';
import useNavigate from '@/hooks/useNavigate';
import useSendMessage from '@/hooks/useSendMessage';
import useDialog from '@/hooks/useDialog';
import { mobileReg, passwordValidReg } from '@/utils/util.regexp';
import { getStorage } from '@/utils/util';
import { loginApi } from '@/apis';
import { UUID } from '@/constants';
import { REGISTER_HELP } from '@/constants/tiptype';

export default {
	name: 'Register',
	setup() {
		const store = useStore();
		const formData = reactive({
			username: '',
			mobile: '',
			code: '',
			password: '',
		});
		const formRef = ref(null);
		const isChecked = ref(true);
		const isLoading = ref(false);
		const showPassword = ref(false);
		const { goBack } = useNavigate();
		const { handleOpenDialog } = useDialog();
		const { hasSendMsg, sendTime, handleSendMsg } = useSendMessage();

		const handleSend = () => {
			formRef.value.validate('mobile').then(({ valid }) => {
				if (valid) {
					handleSendMsg({
						phone: formData.mobile,
						msg_type: 'register',
						uuid: getStorage(UUID),
					});
				}
			});
		};

		const handleRegister = () => {
			formRef.value.validate().then(async ({ valid }) => {
				if (valid) {
					try {
						const { username, mobile, password, code } = formData;
						isLoading.value = true;
						await loginApi.register({
							username,
							phone: mobile,
							code,
							password: MD5(password).toString(),
							uuid: getStorage(UUID),
						});
						isLoading.value = false;
						Toast.success('注册成功', {
							duration: 1000,
							onClose() {
								store.dispatch('user/logout');
							},
						});
					} catch (error) {
						isLoading.value = false;
					}
				}
			});
		};

		const handleCustomValidate = async () => {
			await formRef.value.validate('password');
		};

		const handleChangePasswordType = () => {
			showPassword.value = !showPassword.value;
		};
		return {
			mobileReg,
			passwordValidReg,
			formData,
			formRef,
			sendTime,
			hasSendMsg,
			isChecked,
			isLoading,
			showPassword,
			REGISTER_HELP,
			handleRegister,
			goBack,
			handleSend,
			handleCustomValidate,
			handleOpenDialog,
			handleChangePasswordType,
		};
	},
};
</script>

<style lang="scss" scoped>
.container {
	margin-top: $navbar-height;
	overflow: hidden;
	background: #fff;
}

.register-form {
	:deep(.nut-form-item__label) {
		line-height: 28px;
	}
	:deep(.nut-cell) {
		&:last-child::after {
			border-bottom: 2px solid #f5f6f7 !important;
		}
	}
}

.input {
	padding: 0;
	border-bottom: none;
	line-height: 28px;
}

.agree {
	@include flex-center;
	justify-content: flex-end;
	text-align: right;
	line-height: 28px;
	line-height: 45px;
	padding: 0 15px;
	font-size: 14px;
	.link,
	.check-icon {
		color: $primary-color;
		padding: 0 2px;
	}
	.check-icon {
		font-size: 18px;
		font-weight: 500;
	}
}

.btn-container {
	.btn {
		margin-bottom: 16px;
	}
}
</style>
